// To see this message, add the following to the `<head>` section in your
// views/layouts/application.html.erb
//
//    <%= vite_client_tag %>
//    <%= vite_javascript_tag 'application' %>
console.log('Vite ⚡️ Rails')

// If using a TypeScript entrypoint file:
//     <%= vite_typescript_tag 'application' %>
//
// If you want to use .jsx or .tsx, add the extension:
//     <%= vite_javascript_tag 'application.jsx' %>

console.log('Visit the guide for more information: ', 'https://vite-ruby.netlify.app/guide/rails')

// Example: Load Rails libraries in Vite.
//
// import * as Turbo from '@hotwired/turbo'
// Turbo.start()
//
// import ActiveStorage from '@rails/activestorage'
// ActiveStorage.start()
//
// // Import all channels.
// const channels = import.meta.globEager('./**/*_channel.js')

// Example: Import a stylesheet in app/frontend/index.css
// import '~/index.css'

const ENV_DATA = {
  'env mode': import.meta.env.MODE,
  VITE_APPLICATION_DOMAIN: import.meta.env.VITE_APPLICATION_DOMAIN,
  VITE_API_DOMAIN: import.meta.env.VITE_API_DOMAIN,
}

console.table(ENV_DATA)

import { createApp, defineComponent } from 'vue'
import VueScrollTo from 'vue-scrollto'
import globalComponents from '@/_core/globalComponents'
import { importMountComponent, path, viewProps } from '@/_core/pageSelector'
import { datadogService } from '@/services/datadog'
import pinia from '@/stores/index'
import { setLiquidOnImageTags, setAcceptCharsetOnFormTags } from '@/utilities/htmlElementInitilizer'
import bindData from '@/utilities/bindData'

// Initialize Datadog Logs
datadogService.initialize()

// window の error / unhandledrejection イベントは Datadog Browser Logs SDK の forwardErrorsToLogs が
// origin=source として自動収集するため、ここでは購読しない（#8734）。
// 動的 import の失敗も同じ経路で拾われ、beforeSend が error_type=dynamic_import_failure / warn に分類する

document.addEventListener('DOMContentLoaded', async () => {
  const module = await importMountComponent(path)
  const page = module.default

  if (!page || typeof page === 'string') {
    console.warn(`Page not found: Please create App.vue for pages/${path} and run 'yarn generate:pages'.`)
    return
  }

  const app = createApp(page)

  // store 登録
  app.use(pinia)

  // scrollTo
  app.use(VueScrollTo, {
    container: 'body',
    duration: 500,
    easing: 'ease',
    offset: 0,
  })

  // グローバルコンポーネント登録
  globalComponents.forEach((component: ReturnType<typeof defineComponent>) => {
    app.component(component.name, component)
  })

  // サーバから渡された定数系データの provide
  app.provide('EXTERNAL_URL', viewProps.global.constants?.external_url)
  app.provide('PROFESSIONS', viewProps.global.constants?.professions)
  app.provide('PROFESSION_DOMAINS', viewProps.global.constants?.profession_domains)
  app.provide('INDUSTRIES', viewProps.global.constants?.industries)
  app.provide('SCALES', viewProps.global.constants?.scales)
  app.provide('SCALE_LOWER', viewProps.global.constants?.scale_lower)
  app.provide('SCALE_UPPER', viewProps.global.constants?.scale_upper)
  app.provide('LISTING_STATUSES', viewProps.global.constants?.listing_statuses)
  app.provide('POSITION_LEVELS', viewProps.global.constants?.position_levels)
  app.provide('RECAPTCHA_SITE_KEY', viewProps.global.constants?.recaptcha_site_key)
  app.provide('SOLUTION_CATEGORIES', viewProps.global.constants?.solution_categories)
  app.provide('DOCUMENT_KIND_STATUS_TEXT', viewProps.global.constants?.document_kind_status_text)
  app.provide('KEYWORD_CATEGORIES', viewProps.global.constants?.keyword_categories)
  app.provide('MAIL_STOP_CATEGORIES', viewProps.global.constants?.mail_stop_categories)
  app.provide('INVALID_MAIL_DOMAINS', viewProps.global.constants?.invalid_mail_domains)
  app.provide('THEMES', viewProps.global.constants?.themes)
  app.provide('DOCUMENT_ID_LIST_FOR_CHATBOT', viewProps.global.constants?.document_id_list_for_chatbot)

  // Vue global error handler
  app.config.errorHandler = (error, instance, info) => {
    datadogService.trackError(error as Error, {
      error_type: 'vue_error',
      component_info: info,
      component_name: instance?.$options.name || 'Unknown',
    })
  }

  app.mixin({
    mounted() {
      this.$nextTick(() => {
        if ('getElementsByTagName' in this.$el) {
          // 全 img 要素に data 属性をセットする
          const imgElements = [...this.$el.getElementsByTagName('img')]
          imgElements
            .filter(image => !image.getAttribute('initialized'))
            .forEach(image => {
              setLiquidOnImageTags(image)
              image.setAttribute('initialized', 'true')
            })

          // 全 form 要素に accept-charset 属性をセットする
          const formElements = [...this.$el.getElementsByTagName('form')]
          formElements
            .filter(form => !form.getAttribute('initialized'))
            .forEach(form => {
              setAcceptCharsetOnFormTags(form)
              form.setAttribute('initialized', 'true')
            })
        }
      })
    },
  })
  app.mount('#app')
  assignStoreData()
})

/**
 * store の state 情報にサーバから受け取った ViewProps の 内容を assign する
 */
function assignStoreData() {
  const globalData: GlobalData = viewProps.global
  const piniaValue = pinia.state.value

  /**
   * key 名: ViewProps の global プロパティ配下の key 名
   */
  if (Object.keys(piniaValue).length) {
    const store: GlobalData = {}
    Object.keys(piniaValue).forEach(key => {
      Object.assign(store, { [key]: piniaValue[key].context })
    })

    bindData(store, globalData)
  }
}
